
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check } from "lucide-react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { colorOptions } from "@/utils/colorOptions";

// Form schema
const formSchema = z.object({
  name: z.string().min(1, "Collection name is required"),
  description: z.string().optional(),
  color: z.string().min(1, "Please select a color"),
});

type FormValues = z.infer<typeof formSchema>;

interface EditCollectionFormProps {
  collectionId: string;
  initialName: string;
  initialDescription?: string;
  initialColor?: string;
  onComplete: () => void;
}

const EditCollectionForm = ({ 
  collectionId, 
  initialName, 
  initialDescription = "",
  initialColor = "#FF314E", 
  onComplete 
}: EditCollectionFormProps) => {
  const { user } = useAuth();
  const [selectedColor, setSelectedColor] = useState(initialColor);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Log initial props for debugging
  useEffect(() => {
    console.log("EditCollectionForm props:", { collectionId, initialName, initialDescription, initialColor });
  }, [collectionId, initialName, initialDescription, initialColor]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: initialName,
      description: initialDescription,
      color: initialColor || "#FF314E",
    },
  });

  const handleColorSelect = (color: string) => {
    console.log("Selected color:", color);
    setSelectedColor(color);
    form.setValue("color", color);
  };

  const onSubmit = async (values: FormValues) => {
    console.log("Submitting form with values:", values);
    
    if (!user) {
      toast.error("You must be logged in to edit a collection");
      return;
    }
    
    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from("collections")
        .update({
          name: values.name,
          description: values.description,
          color: values.color,
        })
        .eq("id", collectionId)
        .eq("user_id", user.id);

      if (error) {
        throw error;
      }

      console.log("Collection updated successfully:", { 
        id: collectionId,
        name: values.name,
        description: values.description,
        color: values.color
      });
      
      toast.success("Collection updated successfully");
      
      // Call onComplete after successful update
      if (onComplete && typeof onComplete === 'function') {
        console.log("Calling onComplete callback");
        onComplete();
      }
    } catch (error) {
      console.error("Error updating collection:", error);
      toast.error("Failed to update collection");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Collection Name</FormLabel>
              <FormControl>
                <Input placeholder="My Collection" {...field} className="w-full" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Input placeholder="Add a description (optional)" {...field} className="w-full" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2">
          <FormLabel>Color</FormLabel>
          <div className="grid grid-cols-5 gap-4 md:grid-cols-10">
            {colorOptions.map((color) => (
              <div
                key={color.name}
                className={`h-11 w-11 rounded-lg cursor-pointer flex items-center justify-center ${
                  selectedColor === color.value ? "ring-2 ring-offset-2 ring-clustr-blue" : ""
                }`}
                style={{ 
                  backgroundColor: color.value,
                }}
                onClick={() => handleColorSelect(color.value)}
                title={color.name}
              >
                {selectedColor === color.value && (
                  <div className="bg-white rounded-full p-1">
                    <Check className="h-3 w-3 text-black" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            className="bg-clustr-blue hover:bg-blue-600 transition-colors"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Updating..." : "Update Collection"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default EditCollectionForm;
